import { motion } from "framer-motion";
import { useState, useEffect, useMemo } from "react";
import {
    MdCardGiftcard,
    MdHistory,
    MdRefresh,
    MdSavings,
} from "react-icons/md";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { BASE_API_URL } from "../helpers/Config";
import axios from "axios";
import moment from "moment";
import { TriggerBalanceUpdate } from "../helpers/TriggerBalanceUpdate";
import { getPageStyles, MOTION } from "../helpers/pageTheme";

const SectionCard = ({ styles, children, className = "" }) => (
    <div className={`p-3 ${className}`} style={styles.card}>
        {children}
    </div>
);

const CommissionClaim = ({ theme = "dark" }) => {
    const [unclaimedCoins, setUnclaimedCoins] = useState(0);
    const [claimHistory, setClaimHistory] = useState([]);
    const [refreshing, setRefreshing] = useState(false);

    const navigate = useNavigate();
    const isDark = theme === "dark";
    const styles = useMemo(() => getPageStyles(isDark), [isDark]);

    const handleClaim = async () => {
        try {
            const response = await axios.post(`${BASE_API_URL}/team/claim-commission`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
            });
            const data = response?.data;

            if (data?.error) {
                if (data?.error === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                    return;
                }
                toast.error(data?.error);
                return;
            }

            if (data?.msg) {
                toast.success(data?.msg);
                setUnclaimedCoins(0);
                FetchUnclaimed();
                FetchClaimHistory();
            }
            if (data?.balance) {
                TriggerBalanceUpdate({ balance: data?.balance, usdt: data?.usdt });
            }
        } catch {
            toast.error("Failed to claim commission");
        }
    };

    const FetchClaimHistory = async () => {
        try {
            const response = await axios.post(`${BASE_API_URL}/team/commission-claim-report`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
            });
            const data = response?.data;

            if (data?.error) {
                if (data?.error === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                    return;
                }
                toast.error(data?.error);
                return;
            }

            if (data?.data) setClaimHistory(data.data);
        } catch {
            toast.error("Failed to fetch claim history");
        }
    };

    const FetchUnclaimed = async () => {
        try {
            const response = await axios.post(`${BASE_API_URL}/team/unclaimed-commission`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
            });
            const data = response?.data;

            if (data?.error) {
                if (data?.error === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                    return;
                }
                toast.error(data?.error);
                return;
            }

            setUnclaimedCoins(typeof data?.unclaimed === "number" ? data.unclaimed : 0);
        } catch {
            toast.error("Failed to fetch unclaimed balance");
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            await Promise.all([FetchUnclaimed(), FetchClaimHistory()]);
            toast.success("Data refreshed");
        } catch {
            toast.error("Failed to refresh");
        } finally {
            setRefreshing(false);
        }
    };

    useEffect(() => {
        FetchUnclaimed();
        FetchClaimHistory();
    }, []);

    const canClaim = unclaimedCoins >= 10;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="px-3 py-3 overflow-auto"
            style={styles.page}
        >
            <motion.div variants={MOTION.container} initial="hidden" animate="visible" className="d-flex flex-column gap-3">
                <motion.div variants={MOTION.item} style={styles.hero(styles.heroAmber)}>
                    <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
                        <div className="d-flex align-items-center gap-2">
                            <MdSavings size={28} />
                            <div>
                                <h5 className="fw-bold mb-0">Claim Commission</h5>
                                <p className="mb-0 small opacity-75">Convert earned coins to balance</p>
                            </div>
                        </div>
                        <div className="d-flex gap-1">
                            <motion.button
                                type="button"
                                className="btn btn-sm d-flex align-items-center gap-1"
                                style={{
                                    background: "rgba(255,255,255,0.2)",
                                    border: "1px solid rgba(255,255,255,0.3)",
                                    color: "#fff",
                                    borderRadius: "10px",
                                    fontWeight: 600,
                                    fontSize: "0.72rem",
                                }}
                                whileTap={{ scale: 0.96 }}
                                onClick={() => navigate("../commission-record")}
                            >
                                <MdHistory size={16} />
                                Record
                            </motion.button>
                            <motion.button
                                type="button"
                                className="btn btn-sm d-flex align-items-center"
                                style={{
                                    background: "rgba(255,255,255,0.2)",
                                    border: "1px solid rgba(255,255,255,0.3)",
                                    color: "#fff",
                                    borderRadius: "10px",
                                }}
                                whileTap={{ scale: 0.96 }}
                                onClick={handleRefresh}
                                disabled={refreshing}
                            >
                                <motion.span animate={{ rotate: refreshing ? 360 : 0 }} transition={{ duration: 0.8, repeat: refreshing ? Infinity : 0, ease: "linear" }}>
                                    <MdRefresh size={18} />
                                </motion.span>
                            </motion.button>
                        </div>
                    </div>
                    <div className="text-center py-2">
                        <p className="mb-1 small text-uppercase opacity-75" style={{ letterSpacing: "0.08em", fontWeight: 600 }}>
                            Unclaimed coins
                        </p>
                        <div className="display-6 fw-bold">{unclaimedCoins.toFixed(2)}</div>
                        <p className="mb-0 small opacity-75">Minimum 10 coins to claim</p>
                    </div>
                </motion.div>

                <motion.div variants={MOTION.item}>
                    <SectionCard styles={styles}>
                        <motion.button
                            type="button"
                            className="btn w-100"
                            style={{
                                ...styles.submitBtn,
                                opacity: canClaim ? 1 : 0.55,
                                background: canClaim
                                    ? styles.submitBtn.background
                                    : isDark
                                      ? "#374151"
                                      : "#9ca3af",
                                boxShadow: canClaim ? styles.submitBtn.boxShadow : "none",
                            }}
                            whileHover={canClaim ? { scale: 1.02 } : {}}
                            whileTap={canClaim ? { scale: 0.98 } : {}}
                            onClick={handleClaim}
                            disabled={!canClaim}
                        >
                            <MdCardGiftcard className="me-1" size={20} />
                            {canClaim ? "Claim now" : "Insufficient coins"}
                        </motion.button>
                    </SectionCard>
                </motion.div>

                <motion.div variants={MOTION.item}>
                    <SectionCard styles={styles}>
                        <p className="mb-3" style={styles.sectionTitle}>
                            Recent claim history
                        </p>
                        {claimHistory.length === 0 ? (
                            <div style={styles.empty}>No claim history yet</div>
                        ) : (
                            <div style={{ maxHeight: "280px", overflowY: "auto" }}>
                                {claimHistory.map((claim, index) => (
                                    <div
                                        key={claim.claim_id}
                                        className="d-flex justify-content-between align-items-center py-3"
                                        style={{
                                            borderBottom:
                                                index === claimHistory.length - 1
                                                    ? "none"
                                                    : `1px solid ${styles.listBorder}`,
                                        }}
                                    >
                                        <div>
                                            <div className="fw-bold" style={{ color: "#f59e0b" }}>
                                                {claim.amount}
                                            </div>
                                            <small style={{ color: styles.textMuted }}>
                                                {moment(claim.create_date, "YYYY-MM-DD HH:mm:ss").format(
                                                    "DD MMM YYYY, hh:mm A"
                                                )}
                                            </small>
                                        </div>
                                        <span
                                            className="badge"
                                            style={{
                                                backgroundColor: isDark ? "rgba(34,197,94,0.15)" : "#dcfce7",
                                                color: "#22c55e",
                                                fontWeight: 600,
                                            }}
                                        >
                                            Claimed
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </SectionCard>
                </motion.div>
            </motion.div>
        </motion.div>
    );
};

export default CommissionClaim;
